import { ANGLE_STEPS, dist2, iatan2, withinRange } from '../math/fixed.ts';
import { hexToWorld } from '../hex.ts';
import type { BattleState, Enemy } from '../types.ts';
import { ev } from './combat.ts';

/**
 * What comes up (Las vías 4, docs/SAGAS.md): Bruni climbs the rails to the bastion's forge, where he died, and fights
 * among its towers. Now and then he marks the tower nearest him; a hero who gets to it in time keeps it, and one left
 * alone turns on the heroes a while (the towers' own turned shot, battle/unsleeping/turned.ts). Hilde knows where he
 * never covered himself: a blow from under his right arm lands as a critical. With the rails left open, Dorn's
 * automatons come up behind him at half his life and go for the nightmares.
 */

const NEVER = 0x7fffffff;

const bossOf = (s: BattleState): Enemy | undefined => (s.bossId ? s.enemies.find((e) => e.id === s.bossId && e.hp > 0) : undefined);

export const risingTick = (s: BattleState): void => {
  const r = s.rules?.rising;
  if (!r) return;
  const st = (s.rising ??= { next: r.first, marks: [], dorn: 0 });
  if (!s.started) return;
  const boss = bossOf(s);
  if (!boss) {
    st.marks = [];
    return;
  }
  // The marked towers: kept by a hero who got there, turned once the mark runs out.
  st.marks = st.marks.filter((m) => {
    const tw = s.structures.find((x) => x.id === m.id && !x.destroyed);
    if (!tw) return false;
    const c = hexToWorld(tw.q, tw.r);
    if (s.heroes.some((h) => h.downTicks === 0 && withinRange(h.x, h.y, c.x, c.y, r.guard))) {
      ev(s, { t: 'rising', kind: 'saved', id: tw.id, x: c.x, y: c.y });
      return false;
    }
    if (s.tick < m.until) return true;
    tw.turnedUntil = s.tick + r.turn;
    ev(s, { t: 'rising', kind: 'turned', id: tw.id, x: c.x, y: c.y });
    return false;
  });
  // A new mark: the tower nearest him that isn't marked or turned already.
  if (s.waveT >= st.next) {
    st.next = s.waveT + r.every;
    let pick = null;
    let bd = r.range * r.range;
    for (const tw of s.structures) {
      if (tw.destroyed || (tw.turnedUntil ?? 0) > s.tick || st.marks.some((m) => m.id === tw.id)) continue;
      const c = hexToWorld(tw.q, tw.r);
      const d = dist2(c.x, c.y, boss.x, boss.y);
      if (d > bd) continue;
      bd = d;
      pick = tw;
    }
    if (pick) {
      const c = hexToWorld(pick.q, pick.r);
      st.marks.push({ id: pick.id, until: s.tick + r.mark });
      ev(s, { t: 'rising', kind: 'mark', id: pick.id, x: c.x, y: c.y });
    }
  }
  // The rails left open: Dorn's automatons come up the rails at half his life.
  const d = r.dorn;
  if (d && !st.dorn && boss.hp * 2 <= boss.maxHp) {
    st.dorn = s.tick;
    s.bots ??= [];
    for (let i = 0; i < d.n; i++) {
      const x = (i * 2 - (d.n - 1)) * 600;
      const y = -900;
      s.bots.push({
        id: s.nextId++,
        owner: 0,
        pid: '',
        kind: 'guard',
        ally: true,
        x,
        y,
        hp: d.hp,
        maxHp: d.hp,
        damage: d.damage,
        radius: d.radius,
        every: d.every,
        cd: 0,
        expires: NEVER,
        taunt: d.taunt,
        aura: 0,
        auraBp: 0,
        facing: iatan2(boss.y - y, boss.x - x),
        anim: 'idle',
        animT: 0,
        post: { x, y },
      });
    }
    ev(s, { t: 'rising', kind: 'dorn', id: 0, x: 0, y: -900 });
  }
};

/** Whether a hero's blow on the boss comes from under its right arm (its right side, within `arc`). */
export const risingWeak = (s: BattleState, e: Enemy, pid: string): boolean => {
  const r = s.rules?.rising;
  if (!r || !pid || e.id !== s.bossId) return false;
  const h = s.heroes.find((x) => x.pid === pid);
  if (!h) return false;
  const right = e.facing - ANGLE_STEPS / 4;
  const d = ((((iatan2(h.y - e.y, h.x - e.x) - right) % ANGLE_STEPS) + ANGLE_STEPS + ANGLE_STEPS / 2) % ANGLE_STEPS) - ANGLE_STEPS / 2;
  return d <= r.arc && d >= -r.arc;
};
